import { TelemetryClientEnvironment } from "@shapr3d/frontend-lib";

import type { TeamsUserRole } from "./hooks/useAccountTypes";

export type PaddleProductID = number;

declare global {
  interface Window {
    onLoadInlineCheckout: () => void;
  }
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "ms-store-badge": unknown; // Web component extended from input
    }
  }
}

export enum Environment {
  PROD = "prod",
  STAGE = "stage",
  BETA = "beta",
  LOCAL = "local",
  LOCAL_DB = "localdb",
  LOCAL_STAGE = "local-stage",
}

export enum SentryEnvironment {
  PROD = "prod",
  STAGE = "stage",
  BETA = "beta",
  DEV = "dev",
}

const domain = "shapr3d.com";
let syncUrl = "https://sync.api.shapr3d.com";
let webAppUrl = "https://app.shapr3d.com";
let UMSURL = "https://prod.api.shapr3d.com/user-management";
let env = Environment.PROD;
let flagsmithEnvironmentId = "CuoS4dvojMMqZuo76hogNA";
let telemetryEnv = TelemetryClientEnvironment.PROD;
let telemetryUrlOverride: string | undefined;
const GTM_PROD_URL = "https://www.googletagmanager.com/gtm.js?id=GTM-57H228";
const GTM_STAGE_URL = "https://www.googletagmanager.com/gtm.js?id=GTM-5T7679LW";
let tagmanagerUrl = GTM_PROD_URL;
let wvUrls = ["https://collaborate.shapr3d.com", "https://beta.collaborate.shapr3d.com"];
let legacyAccountUrls = ["https://account.shapr3d.com"];
let sentryEnv = SentryEnvironment.PROD;
let reCaptchaSiteKey = "6LezC0scAAAAAPXHQfLHNYMrIDc-I61005JwHxwE";

switch (import.meta.env.VITE_APP_ENV) {
  case Environment.STAGE:
    syncUrl = "https://stage.sync.api.shapr3d.com";
    UMSURL = "https://stage.api.shapr3d.com/user-management";
    webAppUrl = "https://stage.app.shapr3d.com";
    wvUrls = ["https://stage.collaborate.shapr3d.com", "http://localhost:3200"];
    legacyAccountUrls = [];
    env = Environment.STAGE;
    sentryEnv = SentryEnvironment.STAGE;
    reCaptchaSiteKey = "6LfUtEscAAAAABgmpPwtDQg6eV82PAwwNPD-scY4";
    flagsmithEnvironmentId = "9HzvDVezeg48Yexn3c83Db";
    telemetryEnv = TelemetryClientEnvironment.STAGE;
    tagmanagerUrl = GTM_STAGE_URL;
    break;
  case Environment.LOCAL_STAGE:
    syncUrl = "https://stage.sync.api.shapr3d.com";
    UMSURL = "https://stage.api.shapr3d.com/user-management";
    webAppUrl = "http://localhost:3002";
    wvUrls = ["https://stage.collaborate.shapr3d.com", "http://localhost:3200"];
    legacyAccountUrls = ["https://stage.user.shapr3d.com"];
    env = Environment.LOCAL_STAGE;
    sentryEnv = SentryEnvironment.DEV;
    reCaptchaSiteKey = "6LfUtEscAAAAABgmpPwtDQg6eV82PAwwNPD-scY4";
    flagsmithEnvironmentId = "9HzvDVezeg48Yexn3c83Db";
    telemetryEnv = TelemetryClientEnvironment.STAGE;
    tagmanagerUrl = GTM_STAGE_URL;
    break;

  case Environment.BETA:
    syncUrl = "https://beta.sync.api.shapr3d.com";
    webAppUrl = "https://beta.app.shapr3d.com";
    UMSURL = "https://beta.api.shapr3d.com/user-management";
    env = Environment.BETA;
    telemetryEnv = TelemetryClientEnvironment.BETA;
    tagmanagerUrl = GTM_STAGE_URL;
    sentryEnv = SentryEnvironment.BETA;
    break;
  case Environment.LOCAL:
  case Environment.LOCAL_DB:
    syncUrl = "http://localhost:3100";
    UMSURL = "http://localhost:3000";
    webAppUrl = "http://localhost:3002";
    wvUrls = ["http://localhost:3200"];
    legacyAccountUrls = [];
    env = Environment.LOCAL;
    sentryEnv = SentryEnvironment.DEV;
    reCaptchaSiteKey = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";
    flagsmithEnvironmentId = "9HzvDVezeg48Yexn3c83Db";
    telemetryEnv = TelemetryClientEnvironment.DEV;
    tagmanagerUrl = GTM_STAGE_URL;
    break;
}

if (import.meta.env.VITE_PROXY === "true") {
  syncUrl = "/sync-api";
  UMSURL = "/user-management";
  telemetryUrlOverride = "/telemetry/web";
}

export const telemetryClientEnvironment = telemetryEnv;
export const telemetryClientUrlOverride = telemetryUrlOverride;
export const environment = env;
export const syncApiUrl = syncUrl;
export const UMSApiUrl = UMSURL;
export const marketingSiteURLs = ["https://www.shapr3d.com", "https://shapr3d.com"];

export const trackingIDHeaderName = "x-shapr-user-tracking-id";
export const appVersionHeaderName = "x-shapr-app-version";
export const defaultTeamSpaceName = "Untitled Space";
export const defaultProjectTitleName = "Untitled Project";
export const defaultPrivateSpaceName = "Drafts";

export const GTMURL = tagmanagerUrl;

export const remoteConfigEnvironmentId = flagsmithEnvironmentId;
export const remoteConfigTimeoutMS = 1500;
export const cookieDomain = domain;

export const CollaborationPromoURL =
  "https://support.shapr3d.com/hc/en-us/articles/7874521411612-Webviewer";

export const localStorageKeys = {
  isDeepLinkAutoOpenEnabled: "deepLinkAutoOpen",
  featureTourCollaborator: "featureTourCollaborator",
  featureTourMember: "featureTourMember",
  hasSeenIntentPageUserMap: "hasSeenIntentPageUserMap",
  modelLoadingCrashFlag: (viewLinkId: string) => "modelLoadingCrashFlag" + viewLinkId,
  visitedViewLinks: "visitedViewLinks",
} as const;

export const sentryEnvironment = sentryEnv;
export const isDevelopmentOrStageEnvironment =
  environment === Environment.LOCAL ||
  environment === Environment.STAGE ||
  environment === Environment.LOCAL_STAGE;

export const isLocalEnvironment = environment === Environment.LOCAL;

const paddleVendorIDStage = 1531;
const paddleVendorIDProd = 38395;
const paddleYearlyProductIDStage = 10456;
const paddleYearlyProductIDProd = 642667;
export const paddlePlanLiteYearly = "paddle.lite.yearly";
export const paddlePlanLiteMonthly = "paddle.lite.monthly";

export const paddleVendorID =
  import.meta.env.VITE_APP_ENV === "prod" ? paddleVendorIDProd : paddleVendorIDStage;
export const paddleYearlyProductId: PaddleProductID =
  import.meta.env.VITE_APP_ENV === "prod" ? paddleYearlyProductIDProd : paddleYearlyProductIDStage;

export const defaultPaddleTrialDays = 14;
export const chargebeeTrialDays = 14;
export const webAppURL = webAppUrl;
export const webviewerUrls = wvUrls;
export const accountUrls = legacyAccountUrls;
export const legacyURLs = webviewerUrls.concat(accountUrls);
export const zendeskUrl = "https://support.shapr3d.com";
export const zendeskTokenUrl = "https://shapr3d.zendesk.com/access";
export const zendeskNewTicketUrl = "https://support.shapr3d.com/hc/en-us/requests/new";
export const zendeskListTicketsUrl = "https://support.shapr3d.com/hc/en-us/requests";
export const checkoutBillingErrorContactUsURL =
  "https://support.shapr3d.com/hc/en-us/requests/new?ticket_form_id=360001386860";
export const eduLicenseInfoURL = "https://www.shapr3d.com/education";
export const eduLicenseEligibilityURL =
  "https://support.shapr3d.com/hc/en-us/articles/12720247715868-EDU-license-eligibility";
export const teamsArticleURL =
  "https://support.shapr3d.com/hc/en-us/articles/7873779322780-Manage-your-account#manage-teams";
export const teamsMarketingPageURL = "https://www.shapr3d.com/products/teams";
export const supportEmail = "support@shapr3d.com";
export const forwardedTrackingIDHeaderName = "x-shapr-forwarded-tracking-id";
export const platformHeaderName = "x-shapr-platform";
export const recaptchaTokenHeaderName = "x-recaptcha-token";
export const oneLinkBase = "https://shapr3d.onelink.me/clDy";
export const accountDeletionSupportArticleUrl =
  "https://support.shapr3d.com/hc/en-us/articles/360004741633";
export const exportSupportArticleUrl = "https://support.shapr3d.com/hc/en-us/articles/360021173420";
export const appStoreSubscriptionsUrl = "https://apps.apple.com/account/subscriptions";
export const privacyPolicyUrl = "https://www.shapr3d.com/privacy-policy";
export const termsOfUseUrl = "https://www.shapr3d.com/terms-and-conditions";
export const downloadPageUrl = "https://www.shapr3d.com/download";
export const defaultTeamspaceName = "Untitled Space";

export const salesUrl = "https://www.shapr3d.com/business";
export const eduUrl = `${webAppUrl}/edu`;

export const originalIpadAppDownloadURL =
  "https://apps.apple.com/US/app/id1091675654#?platform=ipad";
export const SENTRY_DSN =
  "https://b44261b1a7a24b6bb47e7f8877cecd67@o396075.ingest.sentry.io/5650354";
export const gtmUrl = tagmanagerUrl;
export const ReCAPTCHA_URL =
  "https://recaptcha.net/recaptcha/api.js?onload=recaptchaOnload&render=explicit";
export const downloadInfoURL = "https://download.shapr3d.com/mac/release-meta.json";
export const captchaSiteKey = reCaptchaSiteKey;

const chargebeeSiteNameStage = "shapr3d-test";
const chargebeeSiteNameProd = "shapr3d";
const chargebeePublishableKeyStage = "test_GQfe1RfFKD6tOwFnmZcdcuXdko07QzQwI9";
const chargebeePublishableKeyProd = "live_GNh4qXifn2AJJzfRyubdvL7cuhcd4x3Gyh";

export const chargebeeSiteName =
  import.meta.env.VITE_APP_ENV === "prod" ? chargebeeSiteNameProd : chargebeeSiteNameStage;
export const chargebeePublishableKey =
  import.meta.env.VITE_APP_ENV === "prod"
    ? chargebeePublishableKeyProd
    : chargebeePublishableKeyStage;

// You need to upload this manually to S3
export const downdetectorConfigUrl = "/maintenance.json"; // TODO

export const enableSentryForDevEnvs = import.meta.env.VITE_APP_SENTRY_LOGGING_ENABLED === "true";
export const placeholderWistiaVideoID = "514smhwhv8";

export const sessionStorageKeys = {
  inviteData: "invite-data-for-checkout",
  emailToken: "emailToken",
  checkoutReturnUrl: "checkout-return-url",
} as const;

export type InviteStorageData = {
  emails: string[];
  role: TeamsUserRole;
};

export const queryKeys = {
  authenticatedQueryKey: ["auth"],
} as const;

export const maxVersionNameLength = 150;
